<script lang="ts">
  import { Button } from "$lib/components/ui/button"
  import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "$lib/components/ui/sheet"
  import type { Permission } from "$lib/data/type/permission"
  import { APP_CODE, APP_VERSION } from "$lib/env"
  import { deviceStore } from "$lib/stores/device-store.svelte"
  import CircleCheckBigIcon from "@lucide/svelte/icons/circle-check-big"
  import CircleIcon from "@lucide/svelte/icons/circle"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"

  const device = $derived(deviceStore.device)

  let permissions = $state<Permission[]>(
    deviceStore.permissions.map((permission) => ({ ...permission })),
  )

  function updatePermission(name: string, isGranted: boolean) {
    permissions = permissions.map((permission) => (
      permission.name === name
        ? { ...permission, is_granted: isGranted }
        : permission
    ))
  }

  function handleRequest(permission: Permission) {

    if (permission.name === "geolocation") {

      if (!navigator.geolocation) {
        updatePermission(permission.name, false)
        return
      }

      navigator.geolocation.getCurrentPosition(
        () => updatePermission(permission.name, true),
        () => updatePermission(permission.name, false),
      )
      return
    }

    if (permission.name === "camera") {

      const mediaDevices = navigator.mediaDevices
      if (!mediaDevices?.getUserMedia) {
        updatePermission(permission.name, false)
        return
      }

      void mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          stream.getTracks().forEach((track) => track.stop())
          updatePermission(permission.name, true)
        })
        .catch(() => updatePermission(permission.name, false))
    }

  }

  function handleRefresh() {
    deviceStore.permissionsUpdate(permissions)
  }

</script>

<main class="relative h-full w-full flex items-center justify-center">
  <div class="absolute w-max top-4 inset-e-4 text-end font-mono text-xs font-light text-gray-800">
    <div>{APP_CODE}-{APP_VERSION}</div>
    <div>{device.device_id.toUpperCase().substring(0, 16)}</div>
  </div>
  <section class="relative w-max">
    <div class="relative w-28 text-center">
      <img src={undefined}
           class="hidden absolute size-28 -top-32"
           alt="">
      <LoaderCircleIcon class="size-8 inline-block animate-spin"/>
    </div>
  </section>
</main>

<Sheet open={true}>
  <SheetContent
    side="bottom"
    class="mx-auto grid max-w-sm gap-3 rounded-t-lg"
    showCloseButton={false}
    onInteractOutside={(event) => event.preventDefault()}
    onOpenAutoFocus={(event) => event.preventDefault()}
  >
    <SheetHeader>
      <SheetTitle>Akses Aplikasi</SheetTitle>
      <SheetDescription>
        Untuk menggunakan aplikasi, aplikasi membutuhkan beberapa akses perangkat.
      </SheetDescription>
    </SheetHeader>
    <div>
      {#each permissions as permission (permission.name)}
        <Button
          type="button"
          variant="ghost"
          class="flex w-full items-center justify-between rounded-lg p-3 text-start text-sm"
          onclick={() => handleRequest(permission)}
          disabled={permission.is_granted}
        >
          <span>Akses {permission.label}</span>
          {#if permission.is_granted}
            <CircleCheckBigIcon class="size-5 text-green-500"/>
          {:else}
            <CircleIcon class="size-5 text-gray-500"/>
          {/if}
        </Button>
      {/each}
    </div>
    <SheetFooter>
      <Button
        onclick={handleRefresh}
        disabled={!permissions.every((permission) => permission.is_granted)}
      >Selesai
      </Button>
    </SheetFooter>
  </SheetContent>
</Sheet>